import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSharePoint } from '../../contexts/SharePointContext';
import { useLocation } from 'react-router-dom';
import { documentService } from '../../services/documentService';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import DocumentEditor from './components/DocumentEditor';
import DocumentSidebar from './components/DocumentSidebar';
import CollaborationPanel from './components/CollaborationPanel';
import DocumentToolbar from './components/DocumentToolbar';
import Icon from '../../components/AppIcon';
import SharePointUploadModal from './components/SharePointUploadModal';

const DocumentEditorPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { selectedSite, selectedDrive, isConfigured: sharePointConfigured } = useSharePoint();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCollaboration, setShowCollaboration] = useState(false);
  const [showDocumentSidebar, setShowDocumentSidebar] = useState(true);
  
  // Document data
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [highlightLocation, setHighlightLocation] = useState(null);

  // Document selection state
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Editor state for undo/redo functionality
  const [editorHistory, setEditorHistory] = useState({ past: [], future: [] });
  const [content, setContent] = useState('');

  // NEW: SharePoint upload state
  const [showSharePointModal, setShowSharePointModal] = useState(false);
  const [sharePointUploadResult, setSharePointUploadResult] = useState(null);

  // Load document data - wait for SharePoint if needed
  useEffect(() => {
    if (user) {
      // Small delay to allow SharePointContext to restore from localStorage
      const timer = setTimeout(() => {
        loadDocument();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, location, selectedSite, selectedDrive]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Check for document ID from multiple sources
      let documentId = null;
      let locationToHighlight = null;
      
      // 1. Check React Router state (from AI Analysis navigation)
      if (location?.state?.documentId) {
        documentId = location?.state?.documentId;
        locationToHighlight = location?.state?.highlightLocation;
      }
      
      // 2. Check URL parameters (direct navigation)
      if (!documentId) {
        const urlParams = new URLSearchParams(window.location.search);
        documentId = urlParams?.get('id');
      }
      
      if (!documentId) {
        // Instead of showing error, show document selector
        setShowDocumentSelector(true);
        setLoading(false);
        return;
      }

      // Load document from Supabase
      const documentData = await documentService?.getDocumentById(documentId);
      
      if (!documentData) {
        setError('Document not found');
        return;
      }

      // If it's a SharePoint document, ensure SharePoint is configured
      if (documentData?.storageType === 'sharepoint' || documentData?.storage_type === 'sharepoint') {
        // Import sharepointService to configure it
        const sharepointService = (await import('../../services/sharepointService')).default;
        
        // Check if SharePoint is configured in context
        if (selectedSite?.id && selectedDrive?.id) {
          // Use values from context
          sharepointService?.setSiteId(selectedSite.id);
          sharepointService?.setDriveId(selectedDrive.id);
          console.log('[DOCUMENT_EDITOR] Using SharePoint config from context');
        } else {
          // Try to restore from localStorage
          try {
            const siteStr = localStorage.getItem('zentri_sharepoint_site');
            const driveStr = localStorage.getItem('zentri_sharepoint_drive');
            
            if (siteStr && driveStr) {
              const site = JSON.parse(siteStr);
              const drive = JSON.parse(driveStr);
              
              if (site?.id && drive?.id) {
                sharepointService?.setSiteId(site.id);
                sharepointService?.setDriveId(drive.id);
                console.log('[DOCUMENT_EDITOR] Restored SharePoint config from localStorage:', { siteId: site.id, driveId: drive.id });
              } else {
                console.warn('[DOCUMENT_EDITOR] SharePoint site/drive in localStorage missing IDs');
              }
            } else {
              console.warn('[DOCUMENT_EDITOR] SharePoint document but no configuration found in localStorage');
            }
          } catch (err) {
            console.warn('[DOCUMENT_EDITOR] Could not restore SharePoint config:', err);
          }
        }
      }

      setDocument(documentData);
      setHighlightLocation(locationToHighlight);
      setShowDocumentSelector(false);
      setLastSaved(new Date(documentData?.updatedAt));
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Failed to load document: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDocuments = async () => {
    try {
      setLoadingDocuments(true);
      // Load all documents that user can edit
      const docs = await documentService?.getAllDocuments();
      setAvailableDocuments(docs || []);
    } catch (err) {
      setError('Failed to load documents: ' + err?.message);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocumentSelect = (selectedDocument) => {
    // Navigate to document editor with selected document ID
    const newUrl = `/document-editor?id=${selectedDocument?.id}`;
    window.history?.pushState(null, null, newUrl);
    setDocument(selectedDocument);
    setHighlightLocation(null); // Clear any previous highlight
    setShowDocumentSelector(false);
    setLastSaved(new Date(selectedDocument?.updatedAt));
  };

  const handleSave = async (content) => {
    try {
      setSaving(true);
      // Note: Document content saving would require additional backend implementation
      // For now, just update the last saved timestamp
      setLastSaved(new Date());
    } catch (err) {
      setError('Failed to save document: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handler for content change with history tracking
  const handleContentChange = (newContent) => {
    setEditorHistory(prev => ({
      past: [...prev?.past, content],
      future: []
    }));
    setContent(newContent);
  };

  // Undo/Redo handlers
  const handleUndo = () => {
    if (editorHistory?.past?.length > 0) {
      const previous = editorHistory?.past?.[editorHistory?.past?.length - 1];
      setEditorHistory(prev => ({
        past: prev?.past?.slice(0, -1),
        future: [content, ...prev?.future]
      }));
      setContent(previous);
    }
  };

  const handleRedo = () => {
    if (editorHistory?.future?.length > 0) {
      const next = editorHistory?.future?.[0];
      setEditorHistory(prev => ({
        past: [...prev?.past, content],
        future: prev?.future?.slice(1)
      }));
      setContent(next);
    }
  };

  const handleDownloadDocument = async () => {
    if (!document?.filePath) return;
    
    try {
      const downloadUrl = await documentService?.getDocumentUrl(document?.filePath);
      if (downloadUrl) {
        // Create temporary download link
        const link = window.document?.createElement('a');
        link.href = downloadUrl;
        link.download = document?.name;
        link.style.display = 'none';
        window.document?.body?.appendChild(link);
        link?.click();
        window.document?.body?.removeChild(link);
      }
    } catch (err) {
      setError('Failed to download document: ' + err?.message);
    }
  };

  const handleSharePointUpload = () => {
    setShowSharePointModal(true);
  };

  const handleSharePointUploadSuccess = (result) => {
    setSharePointUploadResult(result);
    // Show success notification
    setError(''); // Clear any previous errors
  };

  // Modified DocumentEditor component to receive highlightLocation
  const renderDocumentEditor = () => {
    if (!document) return null;

    return (
      <DocumentEditor
        document={document}
        onSave={handleSave}
        saving={saving}
        documentType={document?.type || 'document'}
        content={content}
        onChange={handleContentChange}
        highlightLocation={highlightLocation}
        onHighlightCleared={() => setHighlightLocation(null)}
      />
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={32} className="animate-spin text-primary mb-4 mx-auto" />
          <p className="text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // AuthContext will redirect to login
  }

  // Show document selector when no document is selected
  if (showDocumentSelector) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
        <div className="flex pt-16">
          <Sidebar
            isCollapsed={sidebarCollapsed}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />

          <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
            <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] p-8">
              <div className="max-w-2xl w-full">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Icon name="Edit" size={32} className="text-primary" />
                  </div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">Document Editor</h1>
                  <p className="text-muted-foreground">
                    Select a document to start editing, or go to Document Base to upload new documents.
                  </p>
                </div>

                {error && (
                  <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 mb-6 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="AlertTriangle" size={16} />
                      <span className="text-sm">{error}</span>
                      <button 
                        onClick={() => setError('')}
                        className="ml-auto hover:bg-destructive/20 rounded p-1"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Recent Documents</h2>
                    <button
                      onClick={loadAvailableDocuments}
                      disabled={loadingDocuments}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
                    >
                      <Icon name={loadingDocuments ? "Loader" : "RefreshCw"} size={16} className={loadingDocuments ? "animate-spin" : ""} />
                      {loadingDocuments ? "Loading..." : "Refresh"}
                    </button>
                  </div>

                  {availableDocuments?.length > 0 ? (
                    <div className="space-y-3">
                      {availableDocuments?.slice(0, 10)?.map((doc) => (
                        <div
                          key={doc?.id}
                          onClick={() => handleDocumentSelect(doc)}
                          className="flex items-center gap-4 p-4 border border-border rounded-lg hover:bg-muted cursor-pointer transition-colors"
                        >
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Icon 
                              name={doc?.type === 'pdf' ? 'FileText' : doc?.type === 'docx' ? 'File' : 'FileText'} 
                              size={20} 
                              className="text-primary" 
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-foreground truncate">{doc?.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              Modified {new Date(doc?.updatedAt)?.toLocaleDateString()} • {doc?.size ? `${Math.round(doc?.size / 1024)} KB` : 'Unknown size'}
                            </p>
                          </div>
                          <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4 mx-auto">
                        <Icon name="FolderOpen" size={24} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium text-foreground mb-2">No documents found</h3>
                      <p className="text-muted-foreground mb-4">
                        You don't have any documents yet. Upload some documents first to start editing.
                      </p>
                      <button
                        onClick={() => window.location.href = '/document-base-management'}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                      >
                        Go to Document Base
                      </button>
                    </div>
                  )}

                  {!loadingDocuments && availableDocuments?.length === 0 && (
                    <button
                      onClick={loadAvailableDocuments}
                      className="w-full mt-4 px-4 py-2 border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors"
                    >
                      Load Documents
                    </button>
                  )}
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    onClick={() => window.location.href = '/document-base-management'}
                    className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  >
                    <Icon name="Database" size={16} />
                    Browse Documents
                  </button>
                  <button
                    onClick={() => window.location.href = '/dashboard'}
                    className="flex items-center gap-2 px-6 py-3 border border-border rounded-lg text-muted-foreground hover:bg-muted"
                  >
                    <Icon name="Home" size={16} />
                    Back to Dashboard
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error && !document) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4 mx-auto">
            <Icon name="AlertTriangle" size={24} className="text-destructive" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">Failed to Load Document</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            <button 
              onClick={() => window.history?.back()}
              className="px-4 py-2 border border-border rounded-lg text-muted-foreground hover:bg-muted"
            >
              Go Back
            </button>
            <button 
              onClick={() => setShowDocumentSelector(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Select Document
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <div className="flex pt-16">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 mb-4 mx-6 mt-6 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} />
                <span className="text-sm">{error}</span>
                <button 
                  onClick={() => setError('')}
                  className="ml-auto hover:bg-destructive/20 rounded p-1"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="flex h-[calc(100vh-4rem)]">
            {/* Document Sidebar */}
            {showDocumentSidebar && document && (
              <div className="w-80 hidden lg:block">
                <DocumentSidebar
                  document={document}
                  onClose={() => setShowDocumentSidebar(false)}
                  onDownload={handleDownloadDocument}
                  versions={[]}
                  collaborators={[]}
                  comments={[]}
                  onVersionRestore={() => {}}
                  onCommentAdd={() => {}}
                  onCommentReply={() => {}}
                />
              </div>
            )}

            {/* Main Editor Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <DocumentToolbar
                document={document}
                saving={saving}
                lastSaved={lastSaved}
                onSave={handleSave}
                onDownload={handleDownloadDocument}
                onToggleCollaboration={() => setShowCollaboration(!showCollaboration)}
                onToggleSidebar={() => setShowDocumentSidebar(!showDocumentSidebar)}
                showCollaboration={showCollaboration}
                showSidebar={showDocumentSidebar}
                documentType={document?.type || 'document'}
                onExport={() => {}}
                onShare={() => {}}
                autoSaveStatus={saving ? 'saving' : 'saved'}
                isCollaborating={showCollaboration}
                onUndo={handleUndo}
                onRedo={handleRedo}
                canUndo={editorHistory?.past?.length > 0}
                canRedo={editorHistory?.future?.length > 0}
                onSharePointUpload={handleSharePointUpload}
              />

              <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 overflow-hidden">
                  {renderDocumentEditor()}
                </div>

                {/* Collaboration Panel */}
                {showCollaboration && document && (
                  <div className="w-80 hidden lg:block">
                    <CollaborationPanel
                      document={document}
                      onClose={() => setShowCollaboration(false)}
                      activeUsers={[]}
                      recentChanges={[]}
                      suggestions={[]}
                      onInviteUser={() => {}}
                      onAcceptSuggestion={() => {}}
                      onRejectSuggestion={() => {}}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* NEW: SharePoint Upload Modal */}
      <SharePointUploadModal
        isOpen={showSharePointModal}
        onClose={() => setShowSharePointModal(false)}
        document={document}
        onUploadSuccess={handleSharePointUploadSuccess}
      />
    </div>
  );
};

export default DocumentEditorPage;